import {
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
  Checkbox,
  FormLabel,
  FormGroup,
  Button,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import React, { useState, useEffect, ReactNode, useContext, Fragment } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import InputNormal from '../input/InputNormal';
import { autoHypenPhone, makeErrorTextAndPlaceholder, setDateFormat } from '../../utils/tools';
import { CheckBoxOutlineBlankOutlined, Square } from '@mui/icons-material';
import { ModalContext } from '../../provider/ModalProvider';
import validation from '../../utils/validation';
import InfoIcon from '../../components/common/InfoIcon';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import FreeAnnouncementTooltip from '../common/FreeAnnouncementTooltip';

// style
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '29rem',
  height: 'auto',
  boxShadow: 24,
};

interface ITooltipProps extends TooltipProps {
  isZoom?: boolean;
}

const StyledTooltip = styled(({ className, ...props }: ITooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} disableHoverListener />
))(({ placement, isZoom }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#3f3f3f',
    left: placement === 'top-start' ? '1.5rem !important' : '0px !important',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#3f3f3f',
    fontSize: '1rem',
    maxWidth: 'unset',
    transform: isZoom ? 'scale(0.9) !important' : 'none',
  },
  inset: placement === 'top-start' ? 'auto auto 0px -1.6rem !important' : 'unset',
}));

const StyledFormControl = styled(FormControl)({
  borderRadius: '8px',
  padding: '2.5rem 1.5rem 1.25rem 2.5rem',
  width: '100%',
  maxWidth: '500px',
  margin: 'auto',
  backgroundColor: 'var(--white)',
  '&:focus-visible': {
    outline: 'none',
  },
});

const StyledFormLabel = styled(FormLabel)({
  color: 'var(--black)',
  fontSize: '1.125rem',
  marginBottom: '0.75em',
  fontWeight: '700',
  '&.Mui-focused': {
    color: 'var(--black)',
  },
});

const StyledSubtitle = styled(Box)<{ subtitle?: string }>(props => {
  let isBlack = false;
  if (props.subtitle) {
    isBlack = ['(청구일 포함)', '(매년 1월 1일 자동 상향)', '(최근 6개월)'].includes(props.subtitle);
  }
  return {
    fontWeight: '500',
    fontSize: '0.9rem',
    color: isBlack ? 'var(--black)' : 'var(--gray-2)',
  };
});

const StyledRadioGroup = styled(RadioGroup)(() => ({
  paddingRight: '2.125rem',
  width: '100%',
  maxHeight: '220px',
  overflowX: 'hidden',
  overflowY: 'auto',
  display: 'block',
}));

const StyledFormGroup = styled(FormGroup)(() => ({
  paddingRight: '2.125rem',
  width: '100%',
  maxHeight: '220px',
  overflowX: 'hidden',
  overflowY: 'auto',
  display: 'block',
}));

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  width: '100%',
  color: 'var(--gray-1)',
  marginTop: '0.25rem',
  '&.MuiFormControlLabel-root': {
    marginRight: '1rem',
    marginLeft: '1rem',
  },
  '& .MuiTypography-root': {
    fontSize: '1.125rem',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    marginRight: 'auto',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      '& > span:first-of-type': {
        width: '20%',
      },
      '& > span:last-of-type': {
        fontSize: '0.85rem',
      },
    },
  },

  '.disabled_box': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const StyledRadio = styled(Radio)(() => ({
  '&.MuiRadio-root': {
    padding: '7px',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--gray-1)',
  },
  '&.Mui-checked': {
    '& .MuiSvgIcon-root': {
      color: 'var(--green)',
    },
  },
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  '&.MuiCheckbox-root': {
    padding: '7px',

    '.checkbox_square': {
      color: '#e0e0e0',
    },
  },
}));

const StyledButton = styled(Button)(() => ({
  marginRight: 'calc(1.215rem - (64px / 4) + 2px)',
  marginLeft: 'auto',
  fontSize: '1.125rem',
  marginTop: '0.75rem',
  alignItems: 'start',
  justifyContent: 'center',
  width: '10%',
  height: '2.5rem',
  transition: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },

  '&.scroll': {
    marginRight: 'calc(1.215rem - (64px / 4) + 8px)',
  },
}));

function ModalSelect(props: ModalSelectFormProps) {
  const user_info = useSelector((state: RootState) => state.UserReducer);
  const { modal_alert, modal_confirm } = useContext(ModalContext);
  const [value, setValue] = useState<ModalSelectContentsType[]>([]);
  const [contents, setContents] = useState<ModalSelectContentsType[]>([]);
  const [oldValue, setOldValue] = useState<ModalSelectContentsType[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [noSelect, setNoSelect] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const visible = props.visible;
  const modal_id = props.data?.modal_id;
  const title = props.data?.title;
  const subtitle = props.data?.subtitle;
  const type = props.data?.type;
  const selected_data = props.data?.selectedData;
  const old_value = props.currentInput;
  const changeValue = props.onChange;
  const offModal = props.onClick;
  const onCancel = props.onCancel;
  const isMobile = props.isMobile;
  const tutorialTooltip = props.tutorialTooltip;

  const getDefaultValue = (): string => {
    if (selected_data) {
      let default_value: ModalSelectContentsType | undefined;

      if (modal_id === 'charge_date' || modal_id === 'upcheck' || modal_id === 'presendday') {
        default_value = contents.find(item => {
          return item.name.split('///')[0] === selected_data;
        });
      } else {
        default_value = contents.find(item => item.name === selected_data);
      }
      if (default_value) {
        if (String(default_value.id).includes('direct_')) {
          return default_value.name;
        }
        return default_value.id as string;
      } else {
        return 'etc';
      }
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (visible) {
      const tmp_contents = [...props.data.contents];
      const tmp_old_value = [...old_value];

      if (type == 'checkbox') {
        const idx_arr: number[] = [];
        for (const item of tmp_old_value) {
          const check_idx = tmp_contents.findIndex(content => content.id == item.id);
          if (Number(check_idx) >= 0) {
            idx_arr.push(Number(check_idx));
          }
        }

        const find_all_idx = tmp_contents.findIndex(item => item.id == 'all');
        if (Number(find_all_idx) >= 0 && tmp_old_value.length == tmp_contents.length - 1) {
          idx_arr.push(find_all_idx);
          tmp_old_value.push({ id: 'all', name: tmp_contents[find_all_idx].name });
        }

        const find_all_no_idx = tmp_old_value.findIndex(item => item.id == 'all_no');
        if (Number(find_all_no_idx) >= 0) {
          setNoSelect(true);
        }

        setContents([
          ...tmp_contents.map((contents_item, contents_idx) => {
            return {
              ...contents_item,
              checked: idx_arr.includes(contents_idx),
            };
          }),
        ]);
      } else {
        setContents([...tmp_contents]);

        if (tmp_old_value.length > 0 && `${tmp_old_value[0].id}`.includes('direct_')) {
          setInputValue(`${tmp_old_value[0].name}`);
        }
      }
      setOldValue([...tmp_old_value]);
      setValue([...tmp_old_value]);

      setTimeout(() => {
        const el = document.getElementById('wrap_el');
        if (el) {
          if (el.scrollHeight > el.clientHeight) {
            setHasScroll(true);
          } else {
            setHasScroll(false);
          }
        }
      }, 0);
    } else {
      setHasScroll(false);
      setContents([]);
      setValue([]);
      setInputValue('');
      setNoSelect(false);
    }
  }, [visible]);

  useEffect(() => {
    if (hasScroll) {
      const el = document.getElementById('wrap_el');
      if (!el) return;

      if (oldValue.length > 0) {
        const old_id = oldValue[0].id;
        const old_idx = contents.findIndex(item => item.id == old_id);
        setTimeout(() => {
          if (Number(old_idx) >= 0) {
            const old_value_el = document.getElementById(`radio_${old_idx}`);
            if (old_value_el) {
              el.scrollTo({ top: old_value_el.offsetTop - old_value_el.offsetHeight * 2, behavior: 'smooth' });
            }
          }
        }, 50);
      }
    }
  }, [hasScroll]);

  useEffect(() => {
    // ModalSelect의 label 길이가 매우 길어졌을 때, 말줄임 처리 및 툴팁 표시
    setTimeout(() => {
      if (contents.length) {
        const label_array = Array.from(document.getElementsByClassName('MuiFormControlLabel-label')) as HTMLElement[];

        for (let i = 0; i < label_array.length; i++) {
          const label = label_array[i];
          if (label.offsetWidth < label.scrollWidth) {
            const title = props.data.contents[i]['rname'] || props.data.contents[i]['name'];
            if (typeof title === 'string') {
              label.setAttribute('title', title);
            }
          }
        }
      }
    }, 0);
  }, [contents]);

  const onChangeRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    if (id.includes('direct_')) {
      const type = id.replace(/direct_/gi, '');
      const tmp_contents = [...contents.map(item => ({ ...item }))];
      const direct_content = tmp_contents.find(item => item.id === id);
      const input_type = id.replace(/direct_/, '');
      let target = '';
      let modal_confirm_content = 'set_direct_input';
      let confrim_callback = () => {
        //
      };
      let cancel_callback = () => {
        //
      };
      if (input_type === 'phone') {
        target = '핸드폰 번호를';
        confrim_callback = () => {
          if (direct_content) {
            direct_content.visibleInput = true;
            setValue([{ ...direct_content }]);
          }
          setContents([...tmp_contents]);
        };
        cancel_callback = () => {
          setValue([...value]);
        };
      }
      if (inputValue !== '' && validation[type](inputValue) && direct_content?.visibleInput === false) {
        modal_confirm_content = 'update_direct_input';
      }
      modal_confirm.openModalConfirm('normal', modal_confirm_content, confrim_callback, cancel_callback, target);
    } else {
      const tmp_contents = [...contents.map(item => ({ ...item }))];
      const direct_content = tmp_contents.find(item => String(item.id).includes('direct_'));
      if (direct_content) {
        direct_content.visibleInput = false;
        setContents([...tmp_contents]);
      }
      const find_item = contents.find(item => item.id == id);
      if (find_item) {
        setValue([find_item]);
      }
    }
  };

  const onClickDirectRadio = () => {
    modal_confirm.openModalConfirm(
      'normal',
      'update_direct_input',
      () => {
        const tmp_contents = [...contents.map(item => ({ ...item }))];
        const direct_content = tmp_contents.find(item => String(item.id).includes('direct_'));
        if (direct_content) {
          direct_content.visibleInput = true;
          setContents([...tmp_contents]);
        }
      },
      undefined,
      '핸드폰 번호를',
    );
  };

  const handleInput = (val: string, id: string | number) => {
    const cur_id = `${id}`;
    const vali_type = cur_id.replace(/direct_/gi, '');

    if (['phone'].includes(vali_type)) {
      if (val.length > 13) return;

      const input_num = Number(val.replace(/-/g, ''));
      if (Number.isNaN(input_num)) {
        // 숫자 이외의 값 입력시 //
        modal_alert.openModalAlert('only_number');
        return;
      }
      const check = val.match(/[^0-9|^-]/gi);
      if (check && check.length > 0) {
        return;
      } else {
        const cur_val = autoHypenPhone(val);
        setInputValue(cur_val);

        const tmp_value = [...value];
        // tmp_value[0].name = cur_val;
        // setValue([...tmp_value]);
      }
    }
  };

  const onChangeCheckbox = (check: boolean, i: number) => {
    const val = !check;
    let tmp_value = value;
    const tmp_contents = [...contents];

    tmp_contents[i].checked = val;
    if (!val) {
      if (contents[i].id == 'all') {
        tmp_value = [];
        for (const content of tmp_contents) {
          content.checked = false;
        }
      } else {
        const find_target = tmp_value.findIndex(item => item.id == contents[i].id);

        if (Number(find_target) >= 0) {
          if (tmp_value[find_target].id == 'all_no') {
            setNoSelect(false);
          }

          tmp_value.splice(find_target, 1);
        }

        if (tmp_value.length < contents.length) {
          const find_idx = tmp_value.findIndex(item => item.id == 'all');
          if (Number(find_idx) >= 0) {
            tmp_value.splice(find_idx, 1);

            const find_content_idx = contents.findIndex(item => item.id == 'all');
            if (Number(find_content_idx) >= 0) {
              tmp_contents[find_content_idx].checked = false;
            }
          }
        }
      }
    } else {
      if (contents[i].id == 'all') {
        tmp_value = [...contents.map(item => ({ id: item.id, name: item.name }))];

        for (const content of tmp_contents) {
          content.checked = true;
        }
      } else if (contents[i].id == 'all_no') {
        tmp_value = [contents[i]];

        for (const content of tmp_contents) {
          let cur_checked = false;
          if (content.id == 'all_no') {
            cur_checked = true;
          }

          content.checked = cur_checked;
        }
        setNoSelect(true);
      } else {
        tmp_value.push({ id: contents[i].id, name: contents[i].name });

        if (tmp_value.length == contents.length - 1) {
          const find_idx = contents.findIndex(item => item.id == 'all');
          if (Number(find_idx) >= 0) {
            tmp_value.push({ id: contents[find_idx].id, name: contents[find_idx].name });
            tmp_contents[find_idx].checked = true;
          }
        }
      }
    }
    setValue([
      ...tmp_value.map(item => {
        return {
          ...item,
        };
      }),
    ]);

    setContents([...tmp_contents]);
  };

  const onClick = () => {
    if (checkValue()) {
      const tmp_value = [...value];
      if (type == 'checkbox') {
        const check_all_idx = tmp_value.findIndex(item => item.id == 'all');
        if (Number(check_all_idx) >= 0) tmp_value.splice(check_all_idx, 1);
      }

      if (type == 'radio' && tmp_value.length > 0 && `${tmp_value[0].id}`.includes('direct_')) {
        tmp_value[0].name = inputValue;

        if (`${tmp_value[0].id}` == 'direct_phone') {
          const cur_num = inputValue.replace(/[^0-9]/gi, '');
          const list_num_arr = contents.map(item => item.name.replace(/[^0-9]/gi, ''));
          if (list_num_arr.includes(cur_num)) {
            modal_alert.openModalAlert('write_another_number', undefined);
            return;
          }
        }
      }
      changeValue(tmp_value, isMobile);
      // if (modal_id == 'period_format') return;
    } else {
      if (onCancel) {
        onCancel();
      }
    }
    offModal();
  };
  const checkValue = () => {
    let result = false;
    if (type == 'radio') {
      if (oldValue.length == 0 && value.length > 0 && `${value[0].id}`.includes('direct_')) {
        // 처음 직접 입력 클릭 시,
        const type = (value[0].id as string).replace(/direct_/gi, '');
        const isError = !validation[type](inputValue);
        if (isError || !inputValue) {
          result = false;
        } else {
          result = true;
        }
      } else if (oldValue.length > 0 && value.length > 0) {
        if (oldValue[0].id == value[0].id) {
          if (`${oldValue[0].id}`.includes('direct_')) {
            const type = (value[0].id as string).replace(/direct_/gi, '');
            const isError = !validation[type](inputValue);
            if (oldValue[0].name == inputValue || !inputValue) {
              result = false;
            } else if (isError) {
              result = false;
            } else {
              result = true;
            }
          } else {
            result = false;
          }
        } else if (`${value[0].id}`.includes('direct_')) {
          const type = (value[0].id as string).replace(/direct_/gi, '');
          const isError = !validation[type](inputValue);
          if (isError || !inputValue) {
            result = false;
          } else {
            result = true;
          }
        } else {
          result = true;
        }
      } else {
        if (value.length > 0) result = true;
        else result = false;
      }
    } else if (type == 'checkbox') {
      if (oldValue.length > 0 && value.length > 0) {
        if (oldValue.length == value.length) {
          let check_cnt = 0;
          for (const val of value) {
            const old_idx = oldValue.findIndex(old => old.id == val.id);
            if (Number(old_idx) >= 0) {
              check_cnt++;
            } else {
              result = true;
            }
          }

          if (check_cnt == value.length) {
            result = false;
          }
        } else {
          result = true;
        }
      } else {
        if (oldValue.length > 0) {
          result = true;
        } else {
          if (value.length > 0) result = true;
          else result = false;
        }
      }
    }
    return result;
  };

  const setTooltipOpen = (open: boolean, index: number) => {
    const tmp_contents = [...contents].map((content, idx) => {
      if (idx === index) {
        const newOpen = content.tooltip != undefined ? !content.tooltip.open : open;
        return {
          ...content,
          tooltip: content.tooltip
            ? {
                ...content.tooltip,
                open: newOpen,
              }
            : undefined,
        };
      } else {
        return {
          ...content,
          tooltip: content.tooltip
            ? {
                ...content.tooltip,
                open: false,
              }
            : undefined,
        };
      }
    });
    setContents([...tmp_contents]);
  };

  const onClickOverCount = () => {
    const new_value = [
      {
        id: 'isOver',
        name: 'isOver',
      },
    ];
    changeValue(new_value, isMobile);
    offModal();
  };

  return (
    <Modal
      open={visible}
      sx={{
        zoom: `${user_info.zoom_status}%`,
        '& > .MuiBox-root': {
          '&:focus-visible': {
            outline: 'none',
          },
          boxShadow: 'none',
        },
      }}
      onClose={offModal}
    >
      <Box sx={style}>
        <StyledFormControl>
          <StyledFormLabel>
            <Box>
              {tutorialTooltip ? (
                <FreeAnnouncementTooltip
                  isOpen={tutorialTooltip.isOpen}
                  placement={tutorialTooltip.placement}
                  text={tutorialTooltip.text}
                  sx={{ zIndex: 1301 }}
                >
                  {title}
                </FreeAnnouncementTooltip>
              ) : (
                title
              )}{' '}
              <StyledSubtitle component={'span'} subtitle={subtitle}>
                {subtitle}
              </StyledSubtitle>
            </Box>
          </StyledFormLabel>
          {type === 'radio' ? (
            <StyledRadioGroup onChange={onChangeRadio} defaultValue={getDefaultValue()} id='wrap_el'>
              {contents?.map((v, i) => {
                let contents: ReactNode = null;
                if (`${v.id}`.includes('direct_')) {
                  const type = (v.id as string).replace(/direct_/gi, '');
                  const visible_input = v.visibleInput as boolean;
                  const { placeholder } = makeErrorTextAndPlaceholder(type);
                  contents = (
                    <StyledFormControlLabel
                      id={`radio_${i}`}
                      key={`select_modal_radio_${v.id}`}
                      style={{ pointerEvents: 'none' }}
                      value={v.id}
                      label={
                        visible_input ? (
                          <InputNormal
                            id={'direct_input'}
                            placeholder={placeholder}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              handleInput(e.target.value, v.id);
                            }}
                            inputSx={{
                              margin: 0,
                              input: {
                                '&::placeholder': {
                                  fontSize: '1rem !important',
                                },
                              },
                            }}
                            value={value[0] && value[0].id == v.id ? inputValue : ''}
                            error={value[0] && value[0].id != v.id ? false : !validation[type](inputValue)}
                          />
                        ) : inputValue !== '' && validation[type](inputValue) ? (
                          inputValue
                        ) : (
                          '직접 입력'
                        )
                      }
                      labelPlacement='start'
                      control={
                        <StyledRadio
                          style={{ pointerEvents: 'auto' }}
                          disableRipple={true}
                          onClick={
                            inputValue !== '' && validation[type](inputValue) && visible_input === false
                              ? onClickDirectRadio
                              : undefined
                          }
                          color='green'
                          // checked={visible_input || (value[0] && v.id === value[0].id)}
                          checked={visible_input || (value[0] && v.id === value[0].id) ? true : false}
                        />
                      }
                      sx={{ span: { pointerEvents: 'auto' } }}
                    />
                  );
                } else if (
                  ['upcheck', 'charge_date', 'status', 'modal_select_period', 'presendday'].includes(modal_id)
                ) {
                  const [day, auto_date] = v.name.split('///');
                  let color = '#d4d4d4';

                  let width = 'auto';
                  if (modal_id == 'charge_date') {
                    const cur_date = `${setDateFormat(`${auto_date}`.replace(/[^0-9]/gi, ''))} 00:00:00`;
                    const cur_month = new Date(cur_date).getMonth();
                    const now = new Date().getMonth();
                    width = '3rem';

                    if (now == cur_month) {
                      color = '#9f9f9f';
                    }
                  } else if (modal_id == 'status') {
                    width = '4rem';
                  } else if (modal_id == 'upcheck') {
                    color = '#9f9f9f';
                  } else if (modal_id == 'presendday') {
                    const cur_date = `${setDateFormat(`${auto_date}`.replace(/[^0-9]/gi, ''))} 00:00:00`;
                    // const cur_month = new Date(cur_date).getMonth();
                    // const now = new Date().getMonth();
                    width = 'auto';

                    if (auto_date && auto_date.includes('사전 청구일')) {
                      color = '#9f9f9f';
                    }
                  }
                  contents = (
                    <StyledFormControlLabel
                      id={`radio_${i}`}
                      key={`select_modal_radio_${v.id}`}
                      style={{ pointerEvents: 'none' }}
                      value={v.id}
                      label={
                        v.name ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width }}>{day}</Box>
                            <Box
                              sx={{
                                marginLeft: modal_id == 'presendday' ? '0.5rem' : '0rem',
                                fontSize: '0.925rem',
                                color,
                              }}
                            >
                              &nbsp;{auto_date}
                            </Box>
                          </Box>
                        ) : (
                          ''
                        )
                      }
                      labelPlacement='start'
                      control={<StyledRadio style={{ pointerEvents: 'auto' }} disableRipple={true} color='green' />}
                      sx={{ span: { pointerEvents: 'auto' } }}
                    />
                  );
                } else if (v.id === 'not_linkage') {
                  const tooltip = v.tooltip;
                  contents = (
                    <Box sx={{ position: 'relative' }} key={`select_modal_radio_${v.id}`}>
                      <StyledFormControlLabel
                        id={`radio_${i}`}
                        style={{ pointerEvents: 'none' }}
                        value={v.id}
                        label={v.name ? v.name : ''}
                        labelPlacement='start'
                        control={
                          <StyledRadio
                            style={{ pointerEvents: 'auto' }}
                            disableRipple={true}
                            color='green'
                            checked={value[0] && v.id === value[0].id}
                          />
                        }
                        sx={{ span: { pointerEvents: 'auto' } }}
                      />
                      {tooltip ? (
                        <ClickAwayListener
                          onClickAway={() => {
                            setTooltipOpen(false, i);
                          }}
                        >
                          <Box
                            component={'span'}
                            sx={{
                              ml: '0.25rem',
                              cursor: 'pointer',
                              position: 'absolute',
                              top: 'calc((100% - 0.25rem) * 0.5)',
                              left: '17.5%',
                              transform: 'translateY(-27.5%)',
                            }}
                          >
                            <StyledTooltip
                              title={tooltip.text}
                              placement={tooltip.direction}
                              open={tooltip.open}
                              onClose={() => {
                                setTooltipOpen(false, i);
                              }}
                              isZoom={user_info.zoom_status === 90}
                              onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                                event.stopPropagation();
                                setTooltipOpen(true, i);
                              }}
                            >
                              <Box
                                component={'span'}
                                sx={{
                                  fontSize: '1.182rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: '0.1rem',
                                  marginRight: '0.25rem',
                                }}
                              >
                                <InfoIcon fill='#9f9f9f' width={'1rem'} height='1rem' />
                              </Box>
                            </StyledTooltip>
                          </Box>
                        </ClickAwayListener>
                      ) : null}
                    </Box>
                  );
                } else {
                  contents = (
                    <Fragment key={`select_modal_radio_${v.id}`}>
                      <StyledFormControlLabel
                        id={`radio_${i}`}
                        // key={`select_modal_radio_${v.id}`}
                        style={{ pointerEvents: 'none' }}
                        value={v.id}
                        label={v.name ? v.name : ''}
                        labelPlacement='start'
                        control={
                          <StyledRadio
                            style={{ pointerEvents: 'auto' }}
                            disableRipple={true}
                            color='green'
                            checked={value[0] && v.id === value[0].id}
                          />
                        }
                        sx={{ span: { pointerEvents: 'auto' } }}
                      />
                      {v.id === 'p' ? (
                        <Box
                          sx={{
                            margin: '1rem 0 1rem 1rem',
                            width: 'calc(100% - 0.5rem)',
                            borderBottom: 'solid 1px #dee1de',
                          }}
                        />
                      ) : null}
                    </Fragment>
                  );
                }

                return contents;
              })}
            </StyledRadioGroup>
          ) : (
            <StyledFormGroup id='wrap_el'>
              {contents?.map((v, i) => (
                <StyledFormControlLabel
                  key={`select_modal_check_${v.id}`}
                  style={{ pointerEvents: 'none' }}
                  label={v.name}
                  labelPlacement='start'
                  control={
                    <StyledCheckbox
                      checked={v.checked}
                      onChange={() => onChangeCheckbox(v.checked == true, i)}
                      name={v.name}
                      style={{ pointerEvents: 'auto' }}
                      color='green'
                      icon={
                        noSelect && v.id != 'all_no' ? (
                          <Square className='checkbox_square' />
                        ) : (
                          <CheckBoxOutlineBlankOutlined />
                        )
                      }
                      disableRipple={noSelect && v.id != 'all_no'}
                    ></StyledCheckbox>
                  }
                  disabled={noSelect && v.id != 'all_no'}
                  sx={{ span: { pointerEvents: 'auto' } }}
                />
              ))}
            </StyledFormGroup>
          )}
          {modal_id === 'remain_account_count' ? (
            <Button
              disableRipple
              sx={{
                backgroundColor: '#f4f4f4',
                width: '59%',
                alignSelf: 'center',
                borderRadius: '14px',
                color: 'var(--gray-1)',
                height: '2rem',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#f4f4f4',
                },
              }}
              onClick={onClickOverCount}
            >
              + 수강료를 미리 받았어요.
            </Button>
          ) : null}
          <StyledButton onClick={onClick} disableRipple className={hasScroll ? 'scroll' : ''}>
            {checkValue() ? (
              <Box component={'span'} sx={{ color: 'green.main' }}>
                확인
              </Box>
            ) : (
              <Box component={'span'} sx={{ color: 'black.main' }}>
                취소
              </Box>
            )}
          </StyledButton>
        </StyledFormControl>
      </Box>
    </Modal>
  );
}

export default ModalSelect;
